import { useEffect } from 'react';

export type ToastKind = 'success' | 'error' | 'info' | 'warning';

interface Props {
  kind: ToastKind;
  message: string;
  // When provided, renders a Dismiss button and (if autoDismissMs is set)
  // schedules an auto-dismiss timer. Caller owns the toast's open/closed
  // state, so calling onDismiss removes the toast from the tree.
  onDismiss?: () => void;
  autoDismissMs?: number;
  // Optional primary action (e.g. "View") rendered before Dismiss.
  action?: { label: string; onClick: () => void };
}

const KIND_STYLES: Record<ToastKind, string> = {
  success: 'bg-green-50 border-green-200 text-green-800',
  error: 'bg-red-50 border-red-200 text-red-800',
  info: 'bg-blue-50 border-blue-200 text-blue-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
};

/**
 * Top-of-screen banner for transient confirmations and errors.
 *
 * Positioning: fixed, top-center, max width that respects mobile gutters.
 * The toast is purely presentational - open/closed state lives in the
 * caller, which makes it trivial to drive from a useEffect, a fetch
 * outcome, or a redux-style reducer without coupling the component.
 */
export function Toast({ kind, message, onDismiss, autoDismissMs, action }: Props) {
  useEffect(() => {
    if (!autoDismissMs || !onDismiss) return;
    const t = window.setTimeout(onDismiss, autoDismissMs);
    return () => window.clearTimeout(t);
  }, [autoDismissMs, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md rounded-lg px-4 py-3 shadow-lg border text-sm font-medium flex items-center justify-between gap-3 ${KIND_STYLES[kind]}`}
    >
      <span className="flex-1 min-w-0">{message}</span>
      <div className="flex items-center gap-2 shrink-0">
        {action && (
          <button
            onClick={action.onClick}
            className="text-xs font-semibold underline hover:no-underline px-2 py-1"
          >
            {action.label}
          </button>
        )}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="text-xs underline opacity-70 hover:opacity-100 px-2 py-1"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}
